/**
 * Rectangle tool metadata registration
 * Feature: 049-critique-photo-markup
 */

import React from 'react';
import type { MarkupShape, RectangleShape } from '@/types/markup.types';
import { isRectangleShape } from '@/types/markup.types';
import { registerTool } from '@/utils/markupToolRegistry';
import type { HandlePosition, ToolMetadata, ShapeRenderProps } from '@/utils/markupToolRegistry';
import { RectangleTool } from './RectangleTool';
import { RectangleIcon } from '../ToolIcons';
import { isHandDrawn } from '@/utils/roughRenderer';
import { HandDrawnRectangle } from '../HandDrawnShapes';

/**
 * Rectangle tool metadata
 */
const rectangleToolMetadata: ToolMetadata = {
  type: 'rectangle',
  label: 'Rectangle',
  icon: RectangleIcon,
  Component: RectangleTool,
  zOrder: 0, // Default z-order (render on top of background effects)

  /**
   * Get handle positions for rectangle
   * Handle 1: Top-left corner
   * Handle 2: Bottom-right corner
   */
  getHandles: (shape: MarkupShape): HandlePosition[] => {
    if (!isRectangleShape(shape)) return [];

    return [
      {
        id: 'handle1',
        x: shape.geometry.x,
        y: shape.geometry.y,
        cursor: 'nwse-resize',
        label: 'Resize from top-left corner',
      },
      {
        id: 'handle2',
        x: shape.geometry.x + shape.geometry.width,
        y: shape.geometry.y + shape.geometry.height,
        cursor: 'nwse-resize',
        label: 'Resize from bottom-right corner',
      },
    ];
  },

  /**
   * Update rectangle from handle drag
   *
   * BUG FIX (#480): Now uses image-relative coordinates (0-100 for both X and Y)
   */
  updateFromHandle: (
    shape: MarkupShape,
    handleId: 'handle1' | 'handle2',
    x: number,
    y: number,
    _viewBoxHeight: number // Kept for backwards compatibility, no longer used
  ): MarkupShape => {
    if (!isRectangleShape(shape)) return shape;

    if (handleId === 'handle1') {
      // Dragging top-left corner (image-relative: 0-100)
      const newX = Math.max(0, Math.min(x, shape.geometry.x + shape.geometry.width - 1));
      const newY = Math.max(0, Math.min(y, shape.geometry.y + shape.geometry.height - 1));
      return {
        ...shape,
        geometry: {
          x: newX,
          y: newY,
          width: shape.geometry.x + shape.geometry.width - newX,
          height: shape.geometry.y + shape.geometry.height - newY,
        },
      } as RectangleShape;
    } else {
      // Dragging bottom-right corner (image-relative: 0-100)
      const newWidth = Math.max(1, x - shape.geometry.x);
      const newHeight = Math.max(1, y - shape.geometry.y);
      return {
        ...shape,
        geometry: {
          x: shape.geometry.x,
          y: shape.geometry.y,
          width: Math.min(100 - shape.geometry.x, newWidth),
          height: Math.min(100 - shape.geometry.y, newHeight),
        },
      } as RectangleShape;
    }
  },

  /**
   * Translate rectangle by deltaX and deltaY
   *
   * BUG FIX (#480): Now uses image-relative coordinates (0-100 for both X and Y)
   */
  translateShape: (
    shape: MarkupShape,
    deltaX: number,
    deltaY: number,
    _viewBoxHeight: number // Kept for backwards compatibility, no longer used
  ): MarkupShape => {
    if (!isRectangleShape(shape)) return shape;

    return {
      ...shape,
      geometry: {
        x: Math.max(0, Math.min(100 - shape.geometry.width, shape.geometry.x + deltaX)),
        y: Math.max(0, Math.min(100 - shape.geometry.height, shape.geometry.y + deltaY)),
        width: shape.geometry.width,
        height: shape.geometry.height,
      },
    } as RectangleShape;
  },

  /**
   * Render rectangle shape as SVG
   */
  renderShape: ({ shape, isSelected, onMouseDown, viewBoxHeight }: ShapeRenderProps): React.ReactElement => {
    if (!isRectangleShape(shape)) return React.createElement('g');

    // Calculate responsive fontSize based on smaller dimension
    const smallerDimension = Math.min(100, viewBoxHeight);
    const fontSize = smallerDimension * 0.04;
    const labelRadius = smallerDimension * 0.03;
    const labelOffset = labelRadius + 1; // Offset to move label away from shape edge

    // Calculate image center
    const imageCenterX = 50;
    const imageCenterY = viewBoxHeight / 2;

    // Calculate rectangle center
    const rectCenterX = shape.geometry.x + shape.geometry.width / 2;
    const rectCenterY = shape.geometry.y + shape.geometry.height / 2;

    // Find which edge is closest to image center
    const dx = imageCenterX - rectCenterX;
    const dy = imageCenterY - rectCenterY;

    let labelX: number;
    let labelY: number;

    // Determine closest edge based on direction to image center
    if (Math.abs(dx) > Math.abs(dy)) {
      // Horizontal edges are closer
      if (dx > 0) {
        // Right edge is closer to center
        labelX = shape.geometry.x + shape.geometry.width + labelOffset;
        labelY = rectCenterY;
      } else {
        // Left edge is closer to center
        labelX = shape.geometry.x - labelOffset;
        labelY = rectCenterY;
      }
    } else {
      // Vertical edges are closer
      if (dy > 0) {
        // Bottom edge is closer to center
        labelX = rectCenterX;
        labelY = shape.geometry.y + shape.geometry.height + labelOffset;
      } else {
        // Top edge is closer to center
        labelX = rectCenterX;
        labelY = shape.geometry.y - labelOffset;
      }
    }
    const className = `markup-shape ${isSelected ? 'selected' : ''}`;
    const style: React.CSSProperties = {
      cursor: isSelected ? 'grab' : 'pointer',
      pointerEvents: 'auto',
    };

    // Check if hand-drawn style is enabled
    const useHandDrawn = isHandDrawn(shape.strokeDasharray);

    return React.createElement(
      'g',
      { key: shape.id },
      // Rectangle shape (hand-drawn or standard)
      useHandDrawn
        ? React.createElement(HandDrawnRectangle, {
            x: shape.geometry.x,
            y: shape.geometry.y,
            width: shape.geometry.width,
            height: shape.geometry.height,
            strokeColor: shape.strokeColor,
            strokeWidth: shape.strokeWidth,
            isSelected,
            onMouseDown,
            className,
          })
        : React.createElement('rect', {
            className,
            x: shape.geometry.x,
            y: shape.geometry.y,
            width: shape.geometry.width,
            height: shape.geometry.height,
            stroke: shape.strokeColor,
            strokeWidth: isSelected ? shape.strokeWidth * 1.5 : shape.strokeWidth,
            strokeDasharray: shape.strokeDasharray,
            fill: 'none',
            vectorEffect: 'non-scaling-stroke',
            style,
            onMouseDown,
          }),
      // Shape number label background
      React.createElement('circle', {
        cx: labelX,
        cy: labelY,
        r: labelRadius,
        fill: 'black',
        fillOpacity: '0.5',
        stroke: 'white',
        strokeWidth: '0.3',
        vectorEffect: 'non-scaling-stroke',
        style,
        onMouseDown,
      }),
      // Shape number label text
      React.createElement(
        'text',
        {
          x: labelX,
          y: labelY,
          fontSize: String(fontSize),
          fontWeight: 'bold',
          fill: 'white',
          textAnchor: 'middle',
          dominantBaseline: 'central',
          style: { pointerEvents: 'none', userSelect: 'none' },
        },
        shape.number
      )
    );
  },
};

// Register the tool
registerTool(rectangleToolMetadata);

export default rectangleToolMetadata;
